/**
 * Just enough phone handling to turn what a relative typed into a number a
 * phone or WhatsApp can open. No validation beyond "has enough digits" —
 * people write numbers in every style, and a wrong guess is worse than a
 * plain `tel:` link the phone will sort out itself.
 */

/** Country calling codes for the places this family lives (extend freely). */
const DIAL: Record<string, string> = {
  IL: '972', CA: '1', US: '1', FR: '33', GB: '44', MA: '212', BE: '32', CH: '41', DE: '49', ES: '34', IT: '39', NL: '31', AU: '61', AR: '54', BR: '55', MX: '52', ZA: '27',
};

/** Digits only, with an international prefix where one can be inferred; undefined when it is not a usable number. */
export function normalizePhone(raw: string | undefined, countryCode?: string): string | undefined {
  if (!raw) return undefined;
  const trimmed = raw.trim();
  const plus = trimmed.startsWith('+') || trimmed.startsWith('00');
  let digits = trimmed.replace(/\D/g, '');
  if (trimmed.startsWith('00')) digits = digits.slice(2);
  if (digits.length < 6) return undefined;
  if (plus) return digits;
  // A national number (leading 0, or no prefix at all) gets the person's own country code when we know it.
  const cc = countryCode ? DIAL[countryCode.toUpperCase()] : undefined;
  if (cc) {
    const national = digits.replace(/^0+/, '');
    // Already starts with the country code and is long enough to be international? Leave it.
    if (digits.startsWith(cc) && digits.length >= cc.length + 8) return digits;
    return cc + national;
  }
  return digits;
}

/** `tel:` link — keeps the number as typed (phones handle local formats); undefined when unusable. */
export function telLink(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const cleaned = raw.trim().replace(/[^\d+]/g, '');
  return cleaned.replace(/\D/g, '').length >= 6 ? `tel:${cleaned}` : undefined;
}

/** WhatsApp deep link; needs an international number, so the country code matters here. */
export function whatsappLink(raw: string | undefined, countryCode?: string): string | undefined {
  const n = normalizePhone(raw, countryCode);
  return n ? `https://wa.me/${n}` : undefined;
}

export function mailLink(email: string | undefined): string | undefined {
  const e = email?.trim();
  return e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) ? `mailto:${e}` : undefined;
}
